import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { v4 as uuid } from 'uuid';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { Accordion, Switch, Text } from '@appcues/sonar';
import {
  selectUserProperties,
  userPropertiesShape,
} from 'ext/entities/user-properties';
import { FieldSet } from 'ext/components/ui';
import {
  Shape as ActionsShape,
  conditionalType,
  getConditional,
} from 'lib/actions';
import { selectIsExperienceFirstStep } from 'lib/selectors';
import { TARGET_INTERACTION } from 'lib/trait';
import {
  BUTTON,
  CUSTOM_COMPONENT,
  EMOJI,
  HERO,
  IMAGE,
  IMAGE_WITH_TEXT,
} from 'lib/block';
import {
  selectAllSurveyBlocksUpToCurrentStep,
  BlockContentShape,
} from 'entities/block';
import HelpLabel from 'components/SideBarSettings/Shared/HelpLabel';
import { Controls } from 'components/SideBarSettings/Shared/styled';
import useAccordionClick from 'components/SideBarSettings/Shared/use-accordion-click';
import ActionsConfig from './ActionsConfig';
import ConditionalAction from './conditional/ConditionalAction';
import { ACTION_TYPES, groupedActions } from './helpers';

const getDescription = block =>
  `When users interact with the ${block}, Appcues can advance, dismiss the Flow, redirect users, or launch content.`;

const contextHandler = {
  [BUTTON]: {
    label: 'Button',
    filterActions: [ACTION_TYPES.NO_OP],
  },
  [IMAGE]: {
    label: 'Image tap',
    description: getDescription('image'),
  },
  [HERO]: {
    label: 'Hero tap',
    description: getDescription('hero section'),
  },
  [IMAGE_WITH_TEXT]: {
    label: 'Icon with text tap',
    description: getDescription('icon with text'),
  },
  [EMOJI]: {
    label: 'Emoji tap',
    description: getDescription('emoji'),
  },
  [TARGET_INTERACTION]: {
    label: 'Target interaction',
    description: getDescription('target'),
  },
  [CUSTOM_COMPONENT]: {
    label: 'Custom component',
    description: `The custom component can trigger these actions.`,
  },
};

export const actionContexts = Object.keys(contextHandler);

function Actions({
  actions,
  context,
  hasSurveyBlock,
  userProperties,
  isExperienceFirstStep,
  onActionsChange,
  children,
  allSurveyBlocks,
}) {
  const { mobileConditionalActions } = useFlags();
  const trackAccordion = useAccordionClick();

  const contextConfig = contextHandler[context] || {};

  const conditionalAction = actions.find(
    action => action.type === conditionalType
  );
  const nonConditionalActions = actions.filter(
    action => action.type !== conditionalType
  );

  // If the block already has a conditional action, we need to show the UI for it,
  // but if it gets removed, we won't allow it to be toggled back on.
  const shouldShowConditionalActions =
    mobileConditionalActions || conditionalAction;

  const toggleConditionalAction = () => {
    if (!conditionalAction) {
      onActionsChange({
        actions: [
          getConditional({
            checks: [
              { id: uuid(), condition: {}, actions: [] },
              {
                id: uuid(),
                condition: null,
                actions: nonConditionalActions,
              },
            ],
          }),
        ],
      });
    } else {
      const defaultCase = conditionalAction.config.checks.find(
        check => check.condition === null
      );
      onActionsChange(groupedActions(defaultCase?.actions ?? []));
    }
  };

  return (
    <Accordion.Item value="actions">
      <Accordion.Header>
        <Accordion.Trigger
          onClick={() => trackAccordion(contextConfig.label, 'Actions')}
        >
          Actions
        </Accordion.Trigger>
      </Accordion.Header>
      <Accordion.Content>
        <Controls>
          {contextConfig.description && (
            <Text>{contextConfig.description}</Text>
          )}

          {shouldShowConditionalActions && (
            <FieldSet>
              <Switch
                id="conditional-action"
                checked={!!conditionalAction}
                onCheckedChange={toggleConditionalAction}
                fullWidth
              >
                <Switch.Label htmlFor="conditional-action">
                  Conditional action
                  <HelpLabel>
                    Conditions are evaluated in order, and
                    <br />
                    will trigger the first one that matches
                    <br />
                    the given criteria.
                    <br />
                    Requires SDK versions ≥ 5.0.0
                  </HelpLabel>
                </Switch.Label>
              </Switch>
            </FieldSet>
          )}

          {conditionalAction && (
            <ConditionalAction
              conditionalAction={conditionalAction}
              contextConfig={contextConfig}
              hasSurveyBlock={hasSurveyBlock}
              userProperties={userProperties}
              allSurveyBlocks={allSurveyBlocks}
              isExperienceFirstStep={isExperienceFirstStep}
              onActionsChange={onActionsChange}
            />
          )}
          {!conditionalAction && (
            <ActionsConfig
              actions={actions}
              context={contextConfig}
              hasSurveyBlock={hasSurveyBlock}
              userProperties={userProperties}
              isExperienceFirstStep={isExperienceFirstStep}
              onActionsChange={onActionsChange}
            />
          )}
          {children}
        </Controls>
      </Accordion.Content>
    </Accordion.Item>
  );
}

Actions.propTypes = {
  actions: PropTypes.arrayOf(ActionsShape),
  context: PropTypes.oneOf(actionContexts),
  hasSurveyBlock: PropTypes.bool,
  userProperties: userPropertiesShape,
  isExperienceFirstStep: PropTypes.bool,
  onActionsChange: PropTypes.func,
  children: PropTypes.node,
  allSurveyBlocks: PropTypes.arrayOf(BlockContentShape),
};

const mapStateToProps = state => {
  return {
    isExperienceFirstStep: selectIsExperienceFirstStep(state),
    userProperties: selectUserProperties(state),
    allSurveyBlocks: selectAllSurveyBlocksUpToCurrentStep(state),
  };
};

export default connect(mapStateToProps, null)(Actions);
